import type { StatusTone } from '../../../framework/components/app';

export type KnowledgeScope = 'platform' | 'brief_type' | 'team';

export type KnowledgeAgent = 'brief' | 'gapfinder' | 'illuminator' | 'chat';

export type KnowledgeContentKind =
    | 'methodology'
    | 'example'
    | 'rubric'
    | 'glossary'
    | 'reference'
    | 'org_context'
    | 'generic_template';

export type KnowledgeStatus = 'draft' | 'processing' | 'published' | 'archived' | 'failed';

export interface KnowledgeSourceItem {
    uuid: string;
    scope: KnowledgeScope;
    scope_ref_uuid: string | null;
    title: string;
    body_text: string | null;
    file_name: string | null;
    file_type: string | null;
    file_size: number | null;
    file_url: string | null;
    content_kind: KnowledgeContentKind;
    agent_allowlist: KnowledgeAgent[] | null;
    pin_in_prompt: boolean;
    retrieval_priority: number;
    status: KnowledgeStatus;
    quality_flags: Record<string, unknown> | null;
    quality_score: string | null;
    chunk_count: number;
    token_estimate: number | null;
    retrieval_hit_count: number;
    last_retrieved_at: string | null;
    published_at: string | null;
    metadata: Record<string, unknown> | null;
    created_at: string;
    updated_at: string;
}

export const CONTENT_KIND_OPTIONS: { value: KnowledgeContentKind; label: string }[] = [
    { value: 'methodology', label: 'Methodology' },
    { value: 'example', label: 'Example' },
    { value: 'rubric', label: 'Rubric' },
    { value: 'glossary', label: 'Glossary' },
    { value: 'reference', label: 'Reference' },
    { value: 'org_context', label: 'Org context' },
    { value: 'generic_template', label: 'Generic template' },
];

export type KnowledgeScopeAdmin = 'platform' | 'brief_type';

export interface ContentKindGuide {
    value: KnowledgeContentKind;
    label: string;
    description: string;
    whenToUse: string;
    icon: string;
    /** Where admins usually add this kind */
    typicalOn: KnowledgeScopeAdmin[];
    /** Hide from admin platform / brief-type wizards */
    adminHidden?: boolean;
}

export function knowledgeScopeHeading(scope: KnowledgeScopeAdmin, briefTypeName?: string): string {
    if (scope === 'platform') return 'Platform knowledge';
    return briefTypeName ? `${briefTypeName} knowledge` : 'Brief-type knowledge';
}

export function knowledgeScopeExplanation(scope: KnowledgeScopeAdmin, briefTypeName?: string): string {
    if (scope === 'platform') {
        return 'This source applies to every team and every brief type. Brief-type pages add extra knowledge on top — they do not replace platform rules.';
    }
    const name = briefTypeName || 'this brief type';
    return `This source applies only when someone works on a ${name} brief. Platform knowledge is still included automatically for every request.`;
}

export function contentKindScopeBadge(guide: ContentKindGuide, scope: KnowledgeScopeAdmin): string | null {
    const onPlatform = guide.typicalOn.includes('platform');
    const onBrief = guide.typicalOn.includes('brief_type');
    if (onPlatform && onBrief) return null;
    if (scope === 'platform' && onBrief) return 'Usually per brief type';
    if (scope === 'brief_type' && onPlatform) return 'Usually platform-wide';
    if (scope === 'platform' && onPlatform) return 'Common here';
    if (scope === 'brief_type' && onBrief) return 'Common here';
    return null;
}

export const CONTENT_KIND_GUIDES: ContentKindGuide[] = [
    {
        value: 'methodology',
        label: 'Methodology',
        icon: 'compass',
        description: 'How Xplorit designs research, chooses methods, and structures briefs.',
        whenToUse: 'Quality standards and “how we work” guidance that should shape every AI output.',
        typicalOn: ['platform'],
    },
    {
        value: 'example',
        label: 'Example',
        icon: 'file alternate outline',
        description: 'A strong completed brief, section, or assessment that shows what good looks like.',
        whenToUse: 'Exemplar outputs the AI can mirror for tone, depth, and structure for this brief type.',
        typicalOn: ['brief_type'],
    },
    {
        value: 'rubric',
        label: 'Rubric',
        icon: 'tasks',
        description: 'Scoring criteria, dimension definitions, or pass/fail rules for an agent.',
        whenToUse:
            'Agent scoring rules (GapFinder dimensions, Illuminator verdicts) on Platform; section expectations for a specific brief type on Brief types.',
        typicalOn: ['platform', 'brief_type'],
    },
    {
        value: 'glossary',
        label: 'Glossary',
        icon: 'book',
        description: 'Preferred terms, definitions, and naming conventions.',
        whenToUse: 'Xplorit-wide terminology on Platform; jargon specific to one brief type on Brief types.',
        typicalOn: ['platform', 'brief_type'],
    },
    {
        value: 'reference',
        label: 'Reference',
        icon: 'folder open',
        description: 'Background material the AI may draw on when it is relevant to the user’s question.',
        whenToUse: 'Playbooks and frameworks (platform) or background reading for one brief type. Retrieved by similarity.',
        typicalOn: ['platform', 'brief_type'],
    },
    {
        value: 'org_context',
        label: 'Org context',
        icon: 'building outline',
        description: 'Facts about a specific organisation, team, or market.',
        whenToUse: 'Usually added as team knowledge. Use here only for global onboarding patterns or admin test content.',
        typicalOn: ['brief_type'],
        adminHidden: true,
    },
    {
        value: 'generic_template',
        label: 'Generic template',
        icon: 'clone outline',
        description: 'Blank brief scaffolds with section headings but little substance.',
        whenToUse: 'Not recommended. Generic templates tend to homogenise AI outputs. Teams are blocked from uploading these.',
        typicalOn: ['brief_type'],
        adminHidden: true,
    },
];

export function contentKindGuidesForAdmin(): ContentKindGuide[] {
    return CONTENT_KIND_GUIDES.filter((g) => !g.adminHidden);
}

export function contentKindLabel(kind: KnowledgeContentKind): string {
    return CONTENT_KIND_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
}

export type KnowledgeValidationSeverity = 'high' | 'medium';

export interface KnowledgeValidationWarning {
    code: string;
    message: string;
    severity: KnowledgeValidationSeverity;
}

export interface KnowledgeValidationResult {
    fit_level: 'good' | 'uncertain' | 'poor' | 'unknown';
    warnings: KnowledgeValidationWarning[];
    needs_confirmation: boolean;
    block_upload: boolean;
    preview_excerpt: string;
    word_count: number;
    validated_source: string;
    quality_score: string;
}

export const AGENT_LABELS: Record<KnowledgeAgent, string> = {
    brief: 'Built-to-Brief',
    gapfinder: 'GapFinder',
    illuminator: 'Illuminator',
    chat: 'Chat',
};

/** Agents with a shipped user-facing UI. Chat is reserved for a future feature. */
export const AGENT_OPTIONS: { value: KnowledgeAgent; label: string }[] = [
    { value: 'brief', label: AGENT_LABELS.brief },
    { value: 'gapfinder', label: AGENT_LABELS.gapfinder },
    { value: 'illuminator', label: AGENT_LABELS.illuminator },
];

export function agentLabel(agent: KnowledgeAgent): string {
    return AGENT_LABELS[agent] ?? agent;
}

export function knowledgeStatusTone(status: KnowledgeStatus): StatusTone {
    switch (status) {
        case 'published':
            return 'success';
        case 'processing':
            return 'info';
        case 'archived':
            return 'warn';
        case 'failed':
            return 'danger';
        default:
            return 'neutral';
    }
}

export function isKnowledgeIndexing(status: KnowledgeStatus): boolean {
    return status === 'draft' || status === 'processing';
}

export function formatKnowledgeStatus(status: KnowledgeStatus): string {
    switch (status) {
        case 'published':
            return 'Ready';
        case 'processing':
            return 'Indexing…';
        case 'failed':
            return 'Failed';
        case 'archived':
            return 'Archived';
        default:
            return 'Preparing';
    }
}

export function knowledgeFailedHint(item: KnowledgeSourceItem): string | null {
    if (item.status !== 'failed') return null;
    const raw = item.metadata?.ingest_error;
    if (typeof raw !== 'string' || !raw.trim()) {
        return 'Indexing did not complete. Try Retry.';
    }
    if (raw.includes('no longer available') || raw.includes('No readable text')) {
        return 'Original file is missing. Edit and re-upload the file.';
    }
    if (raw.includes('could not find driver')) {
        return 'Vector database was unavailable. Try Retry.';
    }
    if (raw.includes('Malformed UTF-8')) {
        return 'Text could not be read cleanly. Edit content or re-upload, then Retry.';
    }
    return 'Indexing did not complete. Try Retry.';
}

export function knowledgeKindLabel(kind: KnowledgeContentKind): string {
    return CONTENT_KIND_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
}

export function knowledgeTileIcon(item: KnowledgeSourceItem): string {
    const guide = CONTENT_KIND_GUIDES.find((g) => g.value === item.content_kind);
    if (guide) return guide.icon;
    if (item.file_name) return 'file alternate outline';
    return 'sticky note outline';
}

export function knowledgeSourceMeta(item: KnowledgeSourceItem): string {
    const parts: string[] = [knowledgeKindLabel(item.content_kind)];
    if (item.file_name) {
        parts.push(formatFileSize(item.file_size));
    } else if (item.body_text) {
        parts.push('Text entry');
    }
    if (item.pin_in_prompt) parts.push('Pinned');
    if (item.chunk_count > 0) {
        parts.push(`${item.chunk_count} ${item.chunk_count === 1 ? 'chunk' : 'chunks'}`);
    }
    return parts.join(' · ');
}

export function formatFileSize(bytes: number | null): string {
    if (!bytes || bytes <= 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatKnowledgeDate(value: string | null): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
}
